import React, { FC } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import { ExtendedLead } from '../../../services/types/lead';

const StyledLeadCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  height: '120px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - new Date(date).getTime()) / (1000 * 60));

  if (diffInMinutes < 60) {
    return `${diffInMinutes}m`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d`;
};

interface LeadCardProps {
  lead: ExtendedLead;
  onLeadClick: (clientId: number) => void;
}

const LeadCard: FC<LeadCardProps> = ({ lead, onLeadClick }) => {
  const clientName =
    lead.client?.firstName && lead.client?.lastName
      ? `${lead.client.firstName} ${lead.client.lastName}`
      : lead.client?.phone || 'Cliente sin nombre';

  const brandOfInterest = lead.initialBrandOfInterest || 'Sin marca';
  const modelOfInterest = lead.initialModelOfInterest || 'Sin modelo';

  return (
    <StyledLeadCard onClick={() => onLeadClick(lead.client?.id || 0)}>
      <CardContent sx={{ padding: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
          <Typography variant="body2" fontWeight="bold" noWrap sx={{ flex: 1, pr: 1 }}>
            {clientName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {formatTimeAgo(lead.createdAt)}
          </Typography>
        </Box>

        <Typography variant="caption" color="text.secondary" mb={0.5} noWrap>
          📞 {lead.client?.phone}
        </Typography>

        <Typography variant="caption" color="text.secondary" mb={0.5} noWrap>
          🚗 {brandOfInterest} {modelOfInterest}
        </Typography>

        <Box display="flex" justifyContent="space-between" alignItems="center" mt="auto">
          {(lead.messagesCount || 0) > 0 && (
            <Typography variant="caption" color="text.secondary">
              💬 {lead.messagesCount}
            </Typography>
          )}

          {lead.source && (
            <Chip
              label={lead.source}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '9px',
                height: '18px',
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          )}
        </Box>
      </CardContent>
    </StyledLeadCard>
  );
};

export default LeadCard;
